import { Link } from "@/i18n/routing";
import { ArrowUpRight, Check } from "lucide-react";
import { ImagePlaceholder } from "@/components/ImagePlaceholder";

/** Icon + title + description card, optionally links somewhere. */
export const ServiceCard = ({ icon: Icon, title, text, to, cta = "Learn more" }) => {
  const inner = (
    <>
      <span className="grid h-12 w-12 place-items-center rounded-xl bg-mint text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
        {Icon && <Icon className="h-6 w-6" />}
      </span>
      <h3 className="mt-4 text-lg font-bold text-[hsl(var(--vc-ink))]">{title}</h3>
      <p className="mt-1.5 flex-1 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{text}</p>
      {to && (
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand">
          {cta}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      )}
    </>
  );
  const cls =
    "group flex h-full flex-col rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:vc-shadow-card";
  return to ? (
    <Link to={to} className={cls} data-testid={`service-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{inner}</Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
};

export const LocationCard = ({ name, region, blurb, to }) => (
  <Link
    to={to}
    data-testid={`location-card-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="group flex flex-col overflow-hidden rounded-2xl border border-[hsl(var(--vc-line))] bg-white vc-shadow-soft transition-all duration-300 hover:-translate-y-1 hover:vc-shadow-card"
  >
    <ImagePlaceholder label={name} ratio="aspect-[3/2]" rounded="rounded-none" className="border-0 border-b" />
    <div className="flex flex-1 flex-col p-5">
      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">{region}</span>
      <h3 className="mt-1 text-lg font-bold text-[hsl(var(--vc-ink))]">{name}</h3>
      {blurb && <p className="mt-1.5 flex-1 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{blurb}</p>}
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-brand">
        View support
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </Link>
);

export const StepCard = ({ n, title, text, icon: Icon }) => (
  <div className="relative flex h-full flex-col rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-soft">
    <span className="font-display absolute right-5 top-3 text-4xl font-semibold text-mint" style={{ color: "hsl(var(--vc-mint-200))" }}>{n}</span>
    <span className="relative grid h-12 w-12 place-items-center rounded-xl bg-brand text-white">
      {Icon && <Icon className="h-6 w-6" />}
    </span>
    <h3 className="mt-4 text-base font-bold text-[hsl(var(--vc-ink))]">{title}</h3>
    <p className="mt-1.5 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{text}</p>
  </div>
);

export const FeatureList = ({ items = [], className = "" }) => (
  <ul className={`space-y-3 ${className}`}>
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-mint text-brand">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
        <span className="text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{item}</span>
      </li>
    ))}
  </ul>
);
